/* eslint-disable react/prop-types */
/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable no-nested-ternary */
/**
 * User Activity (Super Risk Admin) — audit trail of what each user did, grouped into sessions.
 *
 * Flow: pick a user (or click one in the overview) → see their sessions in the date range →
 * click a session → see the ordered event timeline (page views + API actions).
 * Data: GET /activity/users, /activity/sessions, /activity/session (SUPER_RISK_ADMIN only).
 */
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import DashboardLayout from "pages/LayoutContainers/DashboardLayout";
import DashboardNavbar from "pages/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import useFetchWithMsal from "hooks/useFetchWithMsal";
import { protectedResources, siteData } from "authConfig";
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  TextField,
  IconButton,
} from "@mui/material";
import Icon from "@mui/material/Icon";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import INDataTable from "components/INDataTable";

const BRAND = "#402899";

function fmtDateTime(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}
function fmtDuration(ms) {
  const s = Math.max(0, Math.round((ms || 0) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m < 60) return `${m}m ${rem}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}
function toIsoStart(dateStr) {
  return dateStr ? new Date(`${dateStr}T00:00:00`).toISOString() : "";
}
function toIsoEnd(dateStr) {
  return dateStr ? new Date(`${dateStr}T23:59:59.999`).toISOString() : "";
}
function daysAgoISODate(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function csvEscape(v) {
  const s = String(v == null ? "" : v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function downloadCsv(filename, rows) {
  const blob = new Blob([rows.map((r) => r.map(csvEscape).join(",")).join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function UserActivity() {
  const { execute } = useFetchWithMsal({ scopes: [siteData?.apiScopes] });
  const executeRef = useRef(execute);
  useEffect(() => {
    executeRef.current = execute;
  });
  const base = protectedResources?.apiTodoList?.endpoint || "";

  const [fromDate, setFromDate] = useState(daysAgoISODate(7));
  const [toDate, setToDate] = useState(daysAgoISODate(0));
  const [eventFilter, setEventFilter] = useState("all"); // all | writes | pageviews | errors

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null); // email

  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);

  const [timelineOpen, setTimelineOpen] = useState(false);
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineSession, setTimelineSession] = useState(null);

  const [error, setError] = useState(null);

  const rangeQuery = useMemo(() => {
    const parts = [];
    if (fromDate) parts.push(`from=${encodeURIComponent(toIsoStart(fromDate))}`);
    if (toDate) parts.push(`to=${encodeURIComponent(toIsoEnd(toDate))}`);
    return parts.length ? `?${parts.join("&")}` : "";
  }, [fromDate, toDate]);

  const loadUsers = useCallback(() => {
    if (!base) return;
    setUsersLoading(true);
    executeRef
      .current("GET", `${base}/activity/users${rangeQuery}`)
      .then((res) => {
        setUsers(Array.isArray(res?.data) ? res.data : []);
        setUsersLoading(false);
      })
      .catch(() => {
        setError("Failed to load user activity.");
        setUsersLoading(false);
      });
  }, [base, rangeQuery]);

  const loadSessions = useCallback(
    (email) => {
      if (!base || !email) return;
      setSessionsLoading(true);
      executeRef
        .current(
          "GET",
          `${base}/activity/sessions?user=${encodeURIComponent(email)}${
            rangeQuery ? `&${rangeQuery.slice(1)}` : ""
          }`
        )
        .then((res) => {
          setSessions(Array.isArray(res?.data) ? res.data : []);
          setSessionsLoading(false);
        })
        .catch(() => {
          setError("Failed to load sessions.");
          setSessionsLoading(false);
        });
    },
    [base, rangeQuery]
  );

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (selectedUser) loadSessions(selectedUser);
  }, [selectedUser, loadSessions]);

  const openTimeline = (session) => {
    setTimelineSession(session);
    setTimelineOpen(true);
    setTimeline([]);
    setTimelineLoading(true);
    executeRef
      .current(
        "GET",
        `${base}/activity/session?user=${encodeURIComponent(
          session.userEmail
        )}&from=${encodeURIComponent(
          new Date(session.start).toISOString()
        )}&to=${encodeURIComponent(new Date(session.end).toISOString())}`
      )
      .then((res) => {
        setTimeline(Array.isArray(res?.data) ? res.data : []);
        setTimelineLoading(false);
      })
      .catch(() => setTimelineLoading(false));
  };

  const filteredTimeline = useMemo(() => {
    if (eventFilter === "all") return timeline;
    return timeline.filter((e) => {
      if (eventFilter === "pageviews") return e.eventType === "pageview";
      if (eventFilter === "writes")
        return e.eventType === "api" && ["POST", "PUT", "PATCH", "DELETE"].includes(e.method);
      if (eventFilter === "errors") return !e.ok;
      return true;
    });
  }, [timeline, eventFilter]);

  const usersColumns = useMemo(
    () => [
      { accessorKey: "userName", header: "Name", Cell: ({ row }) => row.original.userName || "—" },
      {
        accessorKey: "userEmail",
        header: "Email",
        Cell: ({ row }) => row.original.userEmail || "—",
      },
      {
        accessorKey: "role",
        header: "Role",
        Cell: ({ row }) =>
          row.original.role ? (
            <Chip
              label={row.original.role}
              size="small"
              variant="outlined"
              sx={{ borderColor: "#B5BFD0" }}
            />
          ) : (
            "—"
          ),
      },
      { accessorKey: "eventCount", header: "Events" },
      {
        accessorKey: "lastAt",
        header: "Last active",
        Cell: ({ row }) => fmtDateTime(row.original.lastAt),
      },
      {
        id: "view",
        header: "",
        Cell: ({ row }) => (
          <Button
            size="small"
            variant="text"
            sx={{ color: BRAND, textTransform: "none" }}
            onClick={() => setSelectedUser(row.original.userEmail)}
          >
            View sessions
          </Button>
        ),
      },
    ],
    []
  );

  const sessionsColumns = useMemo(
    () => [
      {
        accessorKey: "start",
        header: "Session start",
        Cell: ({ row }) => fmtDateTime(row.original.start),
      },
      {
        accessorKey: "durationMs",
        header: "Duration",
        Cell: ({ row }) => fmtDuration(row.original.durationMs),
      },
      { accessorKey: "eventCount", header: "Events" },
      { accessorKey: "pageviewCount", header: "Screens viewed" },
      { accessorKey: "writeCount", header: "Changes" },
      {
        accessorKey: "errorCount",
        header: "Errors",
        Cell: ({ row }) =>
          row.original.errorCount > 0 ? (
            <Chip label={row.original.errorCount} size="small" color="error" variant="outlined" />
          ) : (
            "0"
          ),
      },
      {
        accessorKey: "screens",
        header: "Areas",
        Cell: ({ row }) => (row.original.screens || []).slice(0, 4).join(", ") || "—",
      },
      {
        accessorKey: "endedByLogout",
        header: "Ended",
        Cell: ({ row }) =>
          row.original.endedByLogout ? (
            <Chip label="Logged out" size="small" color="success" variant="outlined" />
          ) : (
            <Chip
              label="No logout"
              size="small"
              variant="outlined"
              sx={{ borderColor: "#B5BFD0" }}
            />
          ),
      },
      {
        id: "open",
        header: "",
        Cell: ({ row }) => (
          <Button
            size="small"
            variant="text"
            sx={{ color: BRAND, textTransform: "none" }}
            onClick={() => openTimeline(row.original)}
          >
            Timeline
          </Button>
        ),
      },
    ],
    []
  );

  const timelineColumns = useMemo(
    () => [
      { accessorKey: "at", header: "Time", Cell: ({ row }) => fmtDateTime(row.original.at) },
      {
        accessorKey: "eventType",
        header: "Type",
        Cell: ({ row }) => {
          if (row.original.eventType === "logout") {
            return <Chip label="Logout" size="small" color="success" variant="outlined" />;
          }
          if (row.original.eventType === "pageview") {
            return (
              <Chip label="Page" size="small" variant="outlined" sx={{ borderColor: "#B5BFD0" }} />
            );
          }
          return (
            <Chip
              label={row.original.method || "API"}
              size="small"
              color={
                ["POST", "PUT", "PATCH", "DELETE"].includes(row.original.method)
                  ? "primary"
                  : "default"
              }
              variant="outlined"
            />
          );
        },
      },
      {
        accessorKey: "actionLabel",
        header: "Action / Screen",
        Cell: ({ row }) =>
          row.original.eventType === "pageview"
            ? row.original.screenLabel || "Viewed page"
            : row.original.actionLabel || "—",
      },
      {
        accessorKey: "path",
        header: "Path",
        Cell: ({ row }) => (
          <MDTypography variant="caption" sx={{ color: "#607089" }}>
            {row.original.path || "—"}
            {row.original.entityId ? ` · ${row.original.entityId}` : ""}
          </MDTypography>
        ),
      },
      {
        accessorKey: "statusCode",
        header: "Status",
        Cell: ({ row }) =>
          row.original.eventType === "pageview" ? (
            "—"
          ) : (
            <Chip
              label={row.original.statusCode || "?"}
              size="small"
              color={row.original.ok ? "success" : "error"}
              variant="outlined"
            />
          ),
      },
    ],
    []
  );

  const exportTimelineCsv = () => {
    const header = [
      "Time",
      "Type",
      "Action/Screen",
      "Method",
      "Path",
      "EntityId",
      "Status",
      "DurationMs",
    ];
    const rows = filteredTimeline.map((e) => [
      fmtDateTime(e.at),
      e.eventType,
      e.eventType === "pageview" ? e.screenLabel : e.actionLabel,
      e.method,
      e.path,
      e.entityId,
      e.statusCode,
      e.durationMs,
    ]);
    downloadCsv(
      `activity_${(timelineSession?.userEmail || "user").replace(/[^a-z0-9]+/gi, "_")}.csv`,
      [header, ...rows]
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox bgColor="white" padding="15px" borderRadius="8px">
        <Grid
          container
          spacing={2}
          sx={{ justifyContent: "space-between", alignItems: "center", mb: 2 }}
        >
          <Grid item>
            <MDTypography fontWeight="medium" fontSize="21px" sx={{ color: "#263445" }}>
              User Activity
            </MDTypography>
            <MDTypography fontWeight="light" fontSize="15px" sx={{ color: "#8090AA" }}>
              Track what each user did and where they went, grouped by session.
            </MDTypography>
          </Grid>
        </Grid>

        {/* Filters */}
        <Grid container spacing={2} sx={{ alignItems: "center", mb: 1 }}>
          <Grid item xs={12} md={3}>
            <Autocomplete
              size="small"
              options={users.map((u) => u.userEmail)}
              value={selectedUser}
              onChange={(e, v) => setSelectedUser(v || null)}
              renderInput={(params) => <TextField {...params} label="User" />}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              size="small"
              fullWidth
              type="date"
              label="From"
              InputLabelProps={{ shrink: true }}
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              size="small"
              fullWidth
              type="date"
              label="To"
              InputLabelProps={{ shrink: true }}
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              size="small"
              fullWidth
              label="Show in timeline"
              value={eventFilter}
              onChange={(e) => setEventFilter(e.target.value)}
            >
              <MenuItem value="all">All events</MenuItem>
              <MenuItem value="pageviews">Page views only</MenuItem>
              <MenuItem value="writes">Changes only</MenuItem>
              <MenuItem value="errors">Errors only</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              sx={{ color: BRAND, borderColor: BRAND, textTransform: "none" }}
              onClick={loadUsers}
            >
              <Icon sx={{ mr: 0.5 }}>refresh</Icon>
              Refresh
            </Button>
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ my: 2 }}>
            {error}
          </Alert>
        )}

        <hr style={{ border: "none", borderTop: "1px solid #ebedf2", margin: "16px 0" }} />

        {!selectedUser ? (
          <>
            <MDTypography fontWeight="medium" fontSize="16px" sx={{ color: "#263445", mb: 1 }}>
              Users with activity
            </MDTypography>
            <INDataTable
              columns={usersColumns}
              data={users}
              state={{ isLoading: usersLoading }}
              enablePagination={users.length > 10}
            />
          </>
        ) : (
          <>
            <MDBox sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <IconButton
                size="small"
                onClick={() => setSelectedUser(null)}
                aria-label="Back to users"
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              <MDTypography fontWeight="medium" fontSize="16px" sx={{ color: "#263445" }}>
                Sessions — {selectedUser}
              </MDTypography>
            </MDBox>
            <INDataTable
              columns={sessionsColumns}
              data={sessions}
              state={{ isLoading: sessionsLoading }}
              enablePagination={sessions.length > 10}
            />
          </>
        )}
      </MDBox>

      {/* Session timeline */}
      <Dialog open={timelineOpen} onClose={() => setTimelineOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Session timeline
          {timelineSession
            ? ` — ${fmtDateTime(timelineSession.start)} (${fmtDuration(
                timelineSession.durationMs
              )})`
            : ""}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ alignItems: "center", mb: 1 }}>
            <Grid item xs={12} md={4}>
              <TextField
                select
                size="small"
                fullWidth
                label="Filter"
                value={eventFilter}
                onChange={(e) => setEventFilter(e.target.value)}
              >
                <MenuItem value="all">All events</MenuItem>
                <MenuItem value="writes">Changes only</MenuItem>
                <MenuItem value="pageviews">Page views only</MenuItem>
                <MenuItem value="errors">Errors only</MenuItem>
              </TextField>
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                size="small"
                sx={{ color: BRAND, borderColor: BRAND, textTransform: "none" }}
                onClick={exportTimelineCsv}
                disabled={!filteredTimeline.length}
              >
                <Icon sx={{ mr: 0.5 }}>download</Icon>
                Export CSV
              </Button>
            </Grid>
          </Grid>
          <INDataTable
            columns={timelineColumns}
            data={filteredTimeline}
            state={{ isLoading: timelineLoading }}
            enablePagination={filteredTimeline.length > 25}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            sx={{ textTransform: "none" }}
            onClick={() => setTimelineOpen(false)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default UserActivity;
